import React, { useEffect, useState } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { useDispatch, useSelector } from "react-redux";
import { getProducts } from "../../store/AsyncMethods/ProductMethod";

const metrics = [
  {
    metric: "Product Quantity with more than 5 review",
    target: "≥ 10",
    fulfilled: 40,
    unfulfilled: 60,
  },
  {
    metric: "Product Quantity with positive product ratings",
    target: "≥ 10",
    fulfilled: 92,
    unfulfilled: 8,
  },
  {
    metric: "Product Quantity with Good Quality Review",
    target: "≥ 30",
    fulfilled: 3,
    unfulfilled: 97,
  },
];

const reviewFilters = [
  { label: "all", active: true },
  { label: "No Review" },
  { label: "1-4 reviews" },
  { label: "More than 4 reviews" },
];
const ratingFilters = [
  { label: "all", active: true },
  { label: "Negative( less than 3 stars)" },
  { label: "Neutral(3 stars)" },
  { label: "Positive(more than or equal to 4 stars)" },
];
const statusFilters = [
  { label: "all", active: true },
  { label: "Warning" },
  { label: "Healthy" },
  { label: "NoReview" },
];

export default function ReviewAnalysisCenter() {
  const dispatch = useDispatch();
  const products = useSelector(state => state.ProductReducer.products);
  const customerOrderPendingReviews = useSelector(state => state.ProductReducer.customerOrderPendingReviews);
  useEffect(() => {
    if (!products || products.length === 0) {
      dispatch(getProducts());
    }
  }, [dispatch, products]);

  // Map products to table rows
  const productReviews = (products || []).map(product => ({
    id: product.id,
    img: product.image_path && product.all_images?.[0] ? `${product.image_path}${product.all_images[0].image}` : 'https://dummyimage.com/64x64/cccccc/000000&text=No+Image',
    title: product.title || 'Untitled Product',
    reviews: product.total_reviews_all_time || 0,
    rating: product.avg_rating_all_time || 0,
    quality: '0%', // Placeholder, update if you have quality review logic
    action: "Product's Review Detail",
    performance: "Understand your Product Performance",
  }));

  // Dynamic filters based on product data
  const allReviews = products.map(p => p.total_reviews_all_time).filter(v => v !== undefined && v !== null);
  const allRatings = products.map(p => p.avg_rating_all_time).filter(v => v !== undefined && v !== null);
  const allStatuses = products.map(p => p.status).filter(v => v !== undefined && v !== null);

  // Dynamic review filters
  const reviewFilterOptions = [];
  if (allReviews.length > 0) {
    reviewFilterOptions.push({ label: 'all', active: true });
    if (allReviews.some(r => r === 0)) reviewFilterOptions.push({ label: 'No Review' });
    if (allReviews.some(r => r > 0 && r <= 4)) reviewFilterOptions.push({ label: '1-4 reviews' });
    if (allReviews.some(r => r > 4)) reviewFilterOptions.push({ label: 'More than 4 reviews' });
  }

  // Dynamic rating filters
  const ratingFilterOptions = [];
  if (allRatings.length > 0) {
    ratingFilterOptions.push({ label: 'all', active: true });
    if (allRatings.some(r => r < 3)) ratingFilterOptions.push({ label: 'Negative( less than 3 stars)' });
    if (allRatings.some(r => r === 3)) ratingFilterOptions.push({ label: 'Neutral(3 stars)' });
    if (allRatings.some(r => r >= 4)) ratingFilterOptions.push({ label: 'Positive(more than or equal to 4 stars)' });
  }

  // Dynamic status filters (if you have a status field)
  const statusFilterOptions = [];
  if (allStatuses.length > 0) {
    statusFilterOptions.push({ label: 'all', active: true });
    if (allStatuses.some(s => s === 'Warning')) statusFilterOptions.push({ label: 'Warning' });
    if (allStatuses.some(s => s === 'Healthy')) statusFilterOptions.push({ label: 'Healthy' });
    if (allStatuses.some(s => s === 'NoReview')) statusFilterOptions.push({ label: 'NoReview' });
  }

  // Store Review Performance metrics (dynamic)
  const totalProducts = products.length;
  const moreThan5Reviews = products.filter(p => (p.total_reviews_all_time || 0) > 5).length;
  const positiveRatings = products.filter(p => (p.avg_rating_all_time || 0) >= 4).length;
  const goodQualityReview = 0; // Placeholder for now
  const metricsDynamic = [
    {
      metric: "Product Quantity with more than 5 review",
      target: "≥ 10",
      fulfilled: moreThan5Reviews,
      unfulfilled: totalProducts - moreThan5Reviews,
    },
    {
      metric: "Product Quantity with positive product ratings",
      target: "≥ 10",
      fulfilled: positiveRatings,
      unfulfilled: totalProducts - positiveRatings,
    },
    {
      metric: "Product Quantity with Good Quality Review",
      target: "≥ 30",
      fulfilled: goodQualityReview,
      unfulfilled: totalProducts - goodQualityReview,
    },
  ];

  // Product with Review Coverage calculation (using customerOrderPendingReviews)
  const allProductIds = new Set((products || []).map(p => String(p.id)));
  const productIdsWithRatings = new Set(
    (customerOrderPendingReviews || [])
      .filter(r => r.rating && r.rating > 0)
      .map(r => String(r.product_id))
  );
  const coverage = allProductIds.size > 0
    ? ((productIdsWithRatings.size / allProductIds.size) * 100).toFixed(1)
    : "0.0";

  // Positive Seller Rating calculation (using customerOrderPendingReviews)
  const allRatedReviews = (customerOrderPendingReviews || []).filter(
    r => r.rating && r.rating > 0
  );
  const positiveRatedReviews = allRatedReviews.filter(
    r => r.rating === 4 || r.rating === 5
  );
  const positiveSellerRating = allRatedReviews.length > 0
    ? ((positiveRatedReviews.length / allRatedReviews.length) * 100).toFixed(1)
    : "0.0";

  // Filter state
  const [selectedReviewFilter, setSelectedReviewFilter] = useState('all');
  const [selectedRatingFilter, setSelectedRatingFilter] = useState('all');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');

  // Search filter state
  const [searchProductId, setSearchProductId] = useState("");
  const [searchItemName, setSearchItemName] = useState("");
  const [searchSellerSku, setSearchSellerSku] = useState("");

  // Filtering logic for productReviews
  const filteredProductReviews = productReviews.filter(row => {
    // Review filter
    if (selectedReviewFilter === 'No Review' && row.reviews !== 0) return false;
    if (selectedReviewFilter === '1-4 reviews' && !(row.reviews > 0 && row.reviews <= 4)) return false;
    if (selectedReviewFilter === 'More than 4 reviews' && !(row.reviews > 4)) return false;
    // Rating filter
    if (selectedRatingFilter === 'Negative( less than 3 stars)' && !(row.rating < 3)) return false;
    if (selectedRatingFilter === 'Neutral(3 stars)' && row.rating !== 3) return false;
    if (selectedRatingFilter === 'Positive(more than or equal to 4 stars)' && !(row.rating >= 4)) return false;
    // Status filter (if you have a status field)
    if (selectedStatusFilter !== 'all' && row.status !== undefined && row.status !== selectedStatusFilter) return false;
    // Product ID search
    if (searchProductId && String(row.id).toLowerCase().indexOf(searchProductId.toLowerCase()) === -1) return false;
    // Item Name search
    if (searchItemName && (!row.title || row.title.toLowerCase().indexOf(searchItemName.toLowerCase()) === -1)) return false;
    // Seller SKU search (same as product id)
    if (searchSellerSku && String(row.id).toLowerCase().indexOf(searchSellerSku.toLowerCase()) === -1) return false;
    return true;
  });

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Reviews Management</h2>
      {/* Store Review Performance */}
      <Card className="mb-6 rounded-2xl" style={{ background: "#fafbfc" }}>
        <div className="font-bold text-lg mb-1">Store Review Performance</div>
        <div className="text-gray-500 text-sm mb-4">
          Improve product metrics below to be passed, the product performance can be uplifted. This is a summary of your store review performance based on overall reviews received. Product with no review is not counted in the metrics.
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-xl">
            <thead>
              <tr className="text-left text-gray-600 border-b">
                <th className="py-2 px-4 font-semibold">Metrics</th>
                <th className="py-2 px-4 font-semibold">Target</th>
                <th className="py-2 px-4 font-semibold">Product fulfilled</th>
                <th className="py-2 px-4 font-semibold">Product unfulfilled</th>
              </tr>
            </thead>
            <tbody>
              {metricsDynamic.map((row, idx) => (
                <tr key={idx} className="border-b last:border-0">
                  <td className="py-2 px-4">{row.metric} <i className="pi pi-info-circle text-xs text-gray-400 ml-1" /></td>
                  <td className="py-2 px-4">{row.target}</td>
                  <td className="py-2 px-4">{row.fulfilled}</td>
                  <td className="py-2 px-4">{row.unfulfilled}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {/* Product Review Status */}
      <Card className="mb-6 rounded-2xl" style={{ background: "#fafbfc" }}>
        <div className="font-bold text-lg mb-2">Product Review Status</div>
        <div className="flex flex-wrap gap-3 mb-3">
          <div className="flex gap-2 items-center">
            <span className="font-semibold text-sm">Reviews Received</span>
            {reviewFilterOptions.map((f, i) => (
              <span
                key={i}
                className={`px-3 py-1 rounded-full text-xs font-semibold cursor-pointer ${selectedReviewFilter === f.label ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600'}`}
                onClick={() => setSelectedReviewFilter(f.label)}
              >{f.label}</span>
            ))}
          </div>
          <div className="flex gap-2 items-center">
            <span className="font-semibold text-sm">Rating Score</span>
            {ratingFilterOptions.map((f, i) => (
              <span
                key={i}
                className={`px-3 py-1 rounded-full text-xs font-semibold cursor-pointer ${selectedRatingFilter === f.label ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600'}`}
                onClick={() => setSelectedRatingFilter(f.label)}
              >{f.label}</span>
            ))}
          </div>
          {statusFilterOptions.length > 0 && (
            <div className="flex gap-2 items-center">
              <span className="font-semibold text-sm">Status</span>
              {statusFilterOptions.map((f, i) => (
                <span
                  key={i}
                  className={`px-3 py-1 rounded-full text-xs font-semibold cursor-pointer ${selectedStatusFilter === f.label ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600'}`}
                  onClick={() => setSelectedStatusFilter(f.label)}
                >{f.label}</span>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-2 mb-2">
          <InputText placeholder="Product ID" className="w-40" value={searchProductId} onChange={e => setSearchProductId(e.target.value)} />
          <InputText placeholder="Item Name" className="w-40" value={searchItemName} onChange={e => setSearchItemName(e.target.value)} />
          <InputText placeholder="Seller SKU" className="w-40" value={searchSellerSku} onChange={e => setSearchSellerSku(e.target.value)} />
          <InputText placeholder="Store SKU" className="w-40" disabled />
          <Button label="Search" className="p-button-sm p-button-warning" onClick={() => {}} />
        </div>
      </Card>
      {/* Product Review Table */}
      <div className="bg-white rounded-xl shadow-sm p-2">
        <DataTable value={filteredProductReviews} className="rounded-lg" showGridlines responsiveLayout="scroll">
          <Column
            header="Product Quality"
            body={rowData => (
              <div className="flex items-center gap-3 py-2">
                <img src={rowData.img} alt="product" className="w-12 h-12 rounded object-cover border" />
                <div>
                  <div className="font-semibold text-gray-800 text-sm mb-1">{rowData.title}</div>
                  <div className="text-xs text-gray-500">ID: {rowData.id}</div>
                </div>
              </div>
            )}
          />
          <Column field="reviews" header="Reviews Received" />
          <Column
            field="rating"
            header="Rating Score"
            body={rowData => (
              <div className="flex items-center gap-1">
                <span className="font-bold text-base">{rowData.rating}</span>
                <span className="text-orange-500">
                  {Array.from({ length: Math.floor(rowData.rating) }).map((_, i) => (
                    <i key={i} className="pi pi-star-fill"></i>
                  ))}
                  {rowData.rating % 1 !== 0 && <i className="pi pi-star-half"></i>}
                </span>
              </div>
            )}
          />
          <Column field="quality" header="Quality Reviews" />
        </DataTable>
      </div>
    </div>
  );
} 